import { useEffect, useState } from "react";
import { PaperPlaneRight, ChatCircleText, Camera, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

const RATINGS = [
  { key: "energy", label: "Energy" },
  { key: "sleep", label: "Sleep" },
  { key: "stress", label: "Stress (5 = calm)" },
  { key: "training", label: "Training" },
  { key: "nutrition", label: "Nutrition" },
];

function compressImage(file, maxDim = 1024, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export function RatingPicker({ value, onChange, testid }) {
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`w-9 h-9 border font-mono text-sm transition-colors ${
            value === n ? "bg-brand border-brand text-white" : value >= n ? "bg-brand/30 border-brand/40 text-white" : "bg-[#0A0A0A] border-white/10 text-zinc-500 hover:border-white/30"
          }`}
          data-testid={`${testid}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export default function ClientCheckin() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [ratings, setRatings] = useState({ energy: 0, sleep: 0, stress: 0, training: 0, nutrition: 0 });
  const [weight, setWeight] = useState("");
  const [wins, setWins] = useState("");
  const [struggles, setStruggles] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [cur, hist] = await Promise.all([api.get("/checkins/current"), api.get("/checkins")]);
      setCurrent(cur.data);
      setHistory(hist.data);
    } catch (_e) {}
  };
  useEffect(() => { load(); }, []);

  const addPhotos = async (files) => {
    const room = 4 - photos.length;
    const list = Array.from(files).slice(0, room);
    try {
      const compressed = await Promise.all(list.map((f) => compressImage(f)));
      setPhotos((p) => [...p, ...compressed]);
    } catch (_e) {
      toast.error("Couldn't read that image");
    }
  };

  const submit = async () => {
    if (Object.values(ratings).some((v) => !v)) {
      toast.error("Rate all five areas 1–5 first");
      return;
    }
    setSubmitting(true);
    try {
      const { data: resp } = await api.post("/checkins", {
        ...ratings,
        weight_kg: weight ? Number(weight) : null,
        wins: wins || null,
        struggles: struggles || null,
        photos,
      });
      toast.success("Check-in sent to your coach");
      (resp.new_badges || []).forEach((b) => toast.success(`Badge unlocked — ${b.name}!`, { description: b.description }));
      setRatings({ energy: 0, sleep: 0, stress: 0, training: 0, nutrition: 0 });
      setWeight(""); setWins(""); setStruggles(""); setPhotos([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="client-checkin-page">
      <div>
        <div className="tracker text-brand mb-3">[ Weekly check-in ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">CHECK-IN.</h1>
        <p className="text-zinc-400 mt-3">One quick form every week keeps your coach in the loop.</p>
      </div>

      {current && !current.submitted ? (
        <div className="card-flat p-6 sm:p-8 space-y-6" data-testid="checkin-form">
          <div className="tracker text-zinc-500">[ WEEK OF {current.week_of} ]</div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {RATINGS.map((r) => (
              <div key={r.key}>
                <div className="text-sm text-zinc-300 mb-2">{r.label}</div>
                <RatingPicker value={ratings[r.key]} onChange={(v) => setRatings((s) => ({ ...s, [r.key]: v }))} testid={`rating-${r.key}`} />
              </div>
            ))}
            <div>
              <div className="text-sm text-zinc-300 mb-2">Weight (kg, optional)</div>
              <input type="number" step="0.1" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-32 px-3 py-2 border bg-[#0A0A0A] font-mono" data-testid="checkin-weight" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-zinc-300 mb-2">Wins this week</div>
              <textarea value={wins} onChange={(e) => setWins(e.target.value)} rows={3} maxLength={2000} className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm" placeholder="What went well?" data-testid="checkin-wins" />
            </div>
            <div>
              <div className="text-sm text-zinc-300 mb-2">Struggles</div>
              <textarea value={struggles} onChange={(e) => setStruggles(e.target.value)} rows={3} maxLength={2000} className="w-full px-3 py-2 border bg-[#0A0A0A] text-sm" placeholder="What was hard?" data-testid="checkin-struggles" />
            </div>
          </div>
          <div>
            <div className="text-sm text-zinc-300 mb-2">Progress photos (optional, up to 4)</div>
            <div className="flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt={`photo ${i + 1}`} className="w-20 h-20 object-cover border border-white/10" />
                  <button onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-[#FF6B61] text-white rounded-full p-0.5" data-testid={`remove-photo-${i}`}>
                    <X size={12} weight="bold" />
                  </button>
                </div>
              ))}
              {photos.length < 4 && (
                <label className="w-20 h-20 border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-white/40 text-zinc-500" data-testid="add-photo-label">
                  <Camera size={22} weight="duotone" />
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} data-testid="checkin-photo-input" />
                </label>
              )}
            </div>
          </div>
          <button onClick={submit} disabled={submitting} className="btn-primary disabled:opacity-40" data-testid="checkin-submit">
            <PaperPlaneRight size={16} weight="bold" /> {submitting ? "Sending…" : "Send check-in"}
          </button>
        </div>
      ) : current ? (
        <div className="card-flat p-6 border-emerald-500/30 bg-emerald-500/5" data-testid="checkin-done-banner">
          <div className="font-semibold text-emerald-300">This week's check-in is in ✓</div>
          <div className="text-xs text-zinc-400 mt-1">Next one opens Monday. Your coach will reply below once reviewed.</div>
        </div>
      ) : (
        <div className="tracker text-zinc-500">Loading…</div>
      )}

      {/* History */}
      <section>
        <div className="tracker text-zinc-500 mb-3">[ PAST CHECK-INS ]</div>
        {history.length === 0 ? (
          <div className="card-flat p-8 text-center text-zinc-500 tracker" data-testid="checkin-history-empty">No check-ins yet</div>
        ) : (
          <div className="space-y-3">
            {history.map((c) => (
              <div key={c.id} className="card-flat p-5" data-testid={`checkin-history-${c.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="font-semibold">Week of {c.week_of}</div>
                  <span className={`tracker text-[10px] px-2 py-1 border ${c.status === "reviewed" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>
                    {c.status === "reviewed" ? "REVIEWED" : "AWAITING COACH"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400 font-mono">
                  <span>Energy {c.energy}/5</span><span>Sleep {c.sleep}/5</span><span>Stress {c.stress}/5</span>
                  <span>Training {c.training}/5</span><span>Nutrition {c.nutrition}/5</span>
                  {c.weight_kg && <span>{c.weight_kg}kg</span>}
                </div>
                {(c.wins || c.struggles) && (
                  <div className="mt-3 text-sm text-zinc-300 space-y-1">
                    {c.wins && <div><span className="text-emerald-400">Wins:</span> {c.wins}</div>}
                    {c.struggles && <div><span className="text-[#FF6B61]">Struggles:</span> {c.struggles}</div>}
                  </div>
                )}
                {c.trainer_comment && (
                  <div className="mt-3 p-3 bg-brand/10 border border-brand/30 text-sm flex gap-2" data-testid="trainer-comment">
                    <ChatCircleText size={16} weight="duotone" className="text-brand-light shrink-0 mt-0.5" />
                    <div><span className="tracker text-[9px] text-brand-light block mb-1">COACH</span>{c.trainer_comment}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
